import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { scroller } from 'react-scroll';
import { SCROLL_DURATION } from 'site-modules/shared/constants/sub-navigation';
import {
  EDM_LEASE_DEALS_PAGINATION,
  LEASE_DEALS_CARD_LIST_ANCHOR,
} from 'site-modules/lease-deals/constants/lease-deals-constants';
import { usePersistedData } from 'site-modules/lease-deals/hooks/use-persisted-data/use-persisted-data';
import { useUrlFilterSync } from 'site-modules/lease-deals/hooks/use-url-filter-sync/use-url-filter-sync';
import { createQueryParamsFromFilters } from 'site-modules/shared/utils/lease-deals/filter-url-utils';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { PAGE_SIZE } from 'site-modules/shared/constants/lease-deals/lease-deals';

export const usePagination = ({ totalCount: totalCountProp, selectedFilters, setModelValue }) => {
  const [totalCount] = usePersistedData(totalCountProp);

  const totalPageNumber = Math.ceil(totalCount / PAGE_SIZE);
  const { pageNum = [1] } = selectedFilters;
  const currentPageNumber = Math.min(pageNum[0], totalPageNumber);

  const pageStartIndex = (currentPageNumber - 1) * PAGE_SIZE;
  const pageEndIndex = pageStartIndex + PAGE_SIZE;
  const pageStartItemNumber = pageStartIndex + 1;
  const pageEndItemNumber = Math.min(pageEndIndex, totalCount);

  const pathname = useSelector(state => state.pageContext.location.pathname);
  const routeFilters = useSelector(state => state.leaseDeals?.routeFilters);

  const paginationLinkBuilder = useCallback(
    pageNumber =>
      `${pathname}?${createQueryParamsFromFilters({ ...selectedFilters, pageNum: [pageNumber] }, routeFilters)}`,
    [pathname, selectedFilters, routeFilters]
  );

  const onUpdate = useUrlFilterSync(setModelValue);

  const onClick = useCallback(
    (e, pageNumber) => {
      e.preventDefault();
      onUpdate({ pageNum: [pageNumber] });
      scroller.scrollTo(LEASE_DEALS_CARD_LIST_ANCHOR, { smooth: true, duration: SCROLL_DURATION, offset: -140 });

      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          action_name: TrackingConstant.ACTION_VIEW_CONTENT,
          subaction_name: 'view_lease_deals',
          action_category: TrackingConstant.USER_ACTION_CATEGORY,
          action_cause: TrackingConstant.ACTION_CAUSE_LINK_CLICK,
          creative_id: EDM_LEASE_DEALS_PAGINATION,
          value: `${pageNumber}`,
        },
      });
    },
    [onUpdate]
  );

  return {
    totalCount,
    pageStartItemNumber,
    pageEndItemNumber,
    totalPageNumber,
    currentPageNumber,
    paginationLinkBuilder,
    onClick,
  };
};
